import {
    EventTypes,
    LengthUnits,
    MeasureTypes,
    Plans,
    SpeedUnits,
    TimeUnits,
    ScheduleTabs,
    WeightUnits,
    SortDirection,
    PlaybookBrowseTabs,
} from '@labradorsports/constants';
import { objectMerge, objectSet, processParamConfig } from '@labradorsports/utils';

export const States = [
    'AK',
    'AL',
    'AR',
    'AZ',
    'CA',
    'CO',
    'CT',
    'DE',
    'FL',
    'GA',
    'HI',
    'IA',
    'ID',
    'IL',
    'IN',
    'KS',
    'KY',
    'LA',
    'MA',
    'MD',
    'ME',
    'MI',
    'MN',
    'MO',
    'MS',
    'MT',
    'NC',
    'ND',
    'NE',
    'NH',
    'NJ',
    'NM',
    'NV',
    'NY',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VA',
    'VT',
    'WA',
    'WI',
    'WV',
    'WY',
];

export const Provinces = ['AB', 'BC', 'MB', 'NB', 'NL', 'NT', 'NS', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];

export const Countries = ['US', 'CA'];

export const CouponLimit = 1;

export const UnsavedPlayMessage =
    'You have unsaved changes. If you navigate away, these changes will be lost. Are you sure you want to continue?';

export const PlayerType = 'ROSTER_PLAYER';

export const AffiliateCouponCookieName = 'affiliateCode';

export const EventColorClasses = {
    [EventTypes.GAME]: 'event-type-game',
    [EventTypes.PRACTICE]: 'event-type-practice',
    [EventTypes.SCRIMMAGE]: 'event-type-scrimmage',
    [EventTypes.CUSTOM]: 'event-type-custom',
} as const;

export const VideoDomains = ['youtube.com', 'youtu.be', 'vimeo.com', 'hudl.com'];

export const DataTags = {
    POST: 'Post',
    EVENT: 'Event',
    PLAY: 'Play',
    PLAYLIST_ITEM: 'PlaylistItem',
    FOLDER: 'Folder',
    FORMATION: 'Formation',
    CONCEPT: 'Concept',
    NCAA_TEAM: 'NCAATeam',
    SCOUTING_REPORT: 'ScoutingReport',
    BILLING_ENTITY: 'BillingEntity',
    INVOICE: 'Invoice',
    PAYMENT_METHOD: 'PaymentMethod',
    BILLING_DETAILS: 'BillingDetails',
    PROFILE: 'Profile',
    PROFILE_SETTINGS: 'ProfileSettings',
    PROFILE_FLAGS: 'ProfileFlags',
    PROFILE_IMAGE: 'ProfileImage',
    TEAM_DOCUMENT: 'TeamDocument',
    PRACTICE_TEMPLATE: 'PracticeTemplate',
    OWNED_TEAM: 'OwnedTeam',
    OWNED_PROGRAM: 'OwnedProgram',
    JOINED_TEAM: 'JoinedTeam',
    PENDING_TEAM: 'PendingTeam',
    ENTITY_LOGO: 'EntityLogo',
    TEAM_MEMBER: 'TeamMember',
    ROSTER_PERSON: 'RosterPerson',
    DEPTH_CHART: 'DepthChart',
    PLAYER_RATING: 'PlayerRating',
    VIDEO_DETAILS: 'VideoDetails',
    COMMUNITY_PLAY: 'CommunityPlay',
    COMMUNITY_COMMENT: 'CommunityComment',
    COMMUNITY_PROFILE: 'CommunityProfile',
    PENDING_ROSTER: 'PendingRoster',
    EVALUATION_TEMPLATES: 'EvaluationTemplates',
    EVALUATION_TEMPLATE_REVISION: 'EvaluationTemplateRevisions',
    NOTIFICATION: 'Notification',
    GLOBAL_SUPPRESSION: 'GlobalSuppression',
    VIEWED_PLAYS: 'ViewedPlays',
} as const;

export const PlayType = 'PLAY' as const;

export const SortOptions = {
    DEFAULT: 'DEFAULT',
    CREATED_DATE: 'CREATED_DATE',
    NAME: 'NAME',
} as const;

export const PlaybookBrowseViewMode = {
    LIST: 'list',
    CARD: 'card',
} as const;

export interface PlaybookFilter {
    sortBy?: string;
    sortDirection?: string;
    unviewedOnly?: boolean;
    tags?: string[];
    viewMode?: string;
}

const defaultFilter = {
    sortBy: SortOptions.DEFAULT,
    sortDirection: SortDirection.ASC,
    unviewedOnly: false,
    tags: [],
    viewMode: PlaybookBrowseViewMode.CARD,
};

// empty config objects ensure that the parent config is passed to the child paths
// TODO: improve typing
// TODO: change this to use matchers instead of nested config?
export const paramSetup = processParamConfig({
    global: {
        t: {
            stateKey: 'activeTeam',
            initialState: '',
        },
    },
    '/login': {
        d: { stateKey: 'deeplink', initialState: '' },
    },
    '/signup': {
        d: { stateKey: 'deeplink', initialState: '' },
    },
    '/payment/*': {
        l: {
            stateKey: 'invoiceLinkId',
            initialState: '',
        },
        e: {
            stateKey: 'invoiceError',
            initialState: '',
        },
    },
    '/playbook': [
        {},
        {
            '*': {
                p: {
                    stateKey: 'playId',
                    initialState: '',
                },
                f: {
                    stateKey: 'folderId',
                    initialState: '',
                },
                cpg: {
                    stateKey: 'communityPageNumber',
                    initialState: 1,
                    options: {
                        parse: Number,
                    },
                },
                q: {
                    stateKey: 'playbookFilter',
                    initialState: null,
                    options: {
                        parse: (val: string): PlaybookFilter => {
                            try {
                                const parsedValue = val ? JSON.parse(val) : null;

                                return {
                                    sortBy: parsedValue?.sortBy ?? defaultFilter.sortBy,
                                    sortDirection: parsedValue?.sortDirection ?? defaultFilter.sortDirection,
                                    unviewedOnly: parsedValue?.unviewedOnly ?? defaultFilter.unviewedOnly,
                                    tags: parsedValue?.tags ?? defaultFilter.tags,
                                    viewMode: parsedValue?.viewMode ?? defaultFilter.viewMode,
                                };
                            } catch {
                                return defaultFilter;
                            }
                        },
                        serialize: (val: PlaybookFilter): string => {
                            if (
                                val &&
                                (val.sortBy !== SortOptions.DEFAULT ||
                                    val.sortDirection !== SortDirection.ASC ||
                                    val.unviewedOnly ||
                                    val.tags.length > 0 ||
                                    val.viewMode !== defaultFilter.viewMode)
                            ) {
                                const filtered = Object.fromEntries(
                                    Object.entries(val).filter(
                                        ([key, value]) => value.toString() !== defaultFilter[key].toString()
                                    )
                                );
                                return JSON.stringify(filtered);
                            }

                            return '';
                        },
                    },
                },
                bt: {
                    stateKey: 'playbookBrowseTab',
                    initialState: PlaybookBrowseTabs.PLAYS,
                    options: {
                        parse: (value: string): string => {
                            if (Object.values(PlaybookBrowseTabs).includes(value as any)) {
                                return value;
                            }

                            return PlaybookBrowseTabs.PLAYS;
                        },
                    },
                },
                sf: {
                    stateKey: 'selectedFormationId',
                    initialState: '',
                },
                sc: {
                    stateKey: 'selectedConceptId',
                    initialState: '',
                },
            },
            'usage/': {
                vd: {
                    stateKey: 'viewerDetailsPlayId',
                    initialState: '',
                },
            },
        },
    ],
    '/team/player': [
        {},
        {
            '*': {
                p: {
                    stateKey: 'rosterPlayerId',
                    initialState: '',
                },
            },
            'evaluation/*': {
                e: {
                    stateKey: 'playerEvaluationId',
                    initialState: '',
                },
            },
        },
    ],
    '/join-team': {
        k: { stateKey: 'joinTeamCode', initialState: '' },
        j: { stateKey: 'joinTeamId', initialState: '' },
    },
    '/email-action': {
        mode: { stateKey: 'emailAction.mode', initialState: '' },
        oobCode: { stateKey: 'emailAction.oobCode', initialState: '' },
        apiKey: { stateKey: 'emailAction.apiKey', initialState: '' },
        continueUrl: {
            stateKey: 'emailAction.continueUrl',
            initialState: '',
        },
    },
    '/profile/account/evaluations': {
        e: {
            stateKey: 'playerEvaluationId',
            initialState: '',
        },
    },
    '/profile/subscriptions/manage': [
        {},
        {
            'create/': {
                plan: {
                    stateKey: 'billing.creatingBilling.plan',
                    initialState: '',
                    options: {
                        parse: (val: string): string => {
                            if (val in Plans) {
                                return val;
                            }

                            return '';
                        },
                    },
                },
            },
            'edit/*': {
                e: {
                    stateKey: 'editBillingEntityId',
                    initialState: '',
                },
            },
            'billing/': {
                e: {
                    stateKey: 'editBillingEntityId',
                    initialState: '',
                },
            },
        },
    ],
    '/profile/settings/evaluation/template': [
        {
            e: {
                stateKey: 'selectedEvaluationTemplateId',
                initialState: '',
            },
        },
        {
            '*': {},
        },
    ],
    '/profile/subscriptions/billing/team': {
        e: {
            stateKey: 'editBillingEntityId',
            initialState: '',
        },
    },
    '/schedule': [
        {
            st: {
                stateKey: 'shownScheduleTab',
                initialState: ScheduleTabs.FEED,
                options: {
                    parse: (value: string): string => {
                        // Only 2 valid values
                        if (value === ScheduleTabs.PRACTICE_TEMPLATES) return value;

                        return ScheduleTabs.FEED;
                    },
                },
            },
        },
        {
            'series/*': {},
            event: [
                {
                    ev: {
                        stateKey: 'viewEventId',
                        initialState: '',
                    },
                },
                {
                    '*': {},
                    'edit/*': {},
                    'scouting-report/*': {},
                },
            ],
            'practice/*': {
                p: {
                    stateKey: 'viewPracticeTemplateId',
                    initialState: '',
                },
            },
            scouting: [
                {
                    ev: {
                        stateKey: 'viewEventId',
                        initialState: '',
                    },
                },
                {
                    view: {},
                    'edit/*': {},
                },
            ],
        },
    ],
    '/schedule/update-rsvp': {
        r: {
            stateKey: 'viewEventId',
            initialState: '',
        },
        s: {
            stateKey: 'rsvpUpdateStatus',
            initialState: '',
        },
    },
    '/feed/post': {
        p: {
            stateKey: 'viewPostId',
            initialState: '',
        },
    },
    '/documents/acks': {
        d: {
            stateKey: 'viewDocumentAcksId',
            initialState: '',
        },
    },
    '/play/*': {
        l: {
            stateKey: 'publicLinkId',
            initialState: '',
        },
    },
    '/community/latest': {
        pg: {
            stateKey: 'communityPageNumber',
            initialState: 1,
            options: {
                parse: Number,
            },
        },
    },
    '/community/popular': {
        pg: {
            stateKey: 'communityPageNumber',
            initialState: 1,
            options: {
                parse: Number,
            },
        },
    },
    '/community/trending': {
        pg: {
            stateKey: 'communityPageNumber',
            initialState: 1,
            options: {
                parse: Number,
            },
        },
    },
    '/community/play': {
        p: {
            stateKey: 'communityPlayId',
            initialState: '',
        },
    },
    '/community/embed': {
        p: {
            stateKey: 'communityPlayId',
            initialState: '',
        },
    },
    '/community/profile': {
        pr: {
            stateKey: 'communityProfileId',
            initialState: '',
        },
    },
    '/community/search': {
        q: {
            stateKey: 'communitySearchText',
            initialState: '',
        },
        pg: {
            stateKey: 'communityPageNumber',
            initialState: 1,
            options: {
                parse: Number,
            },
        },
    },
});

export const usedQueryParams = Array.from(
    new Set(Object.values(paramSetup).flatMap((routeSetup) => Object.keys(routeSetup)))
);

export const baseParamState = Object.values(paramSetup).reduce((acc, pathConfig) => {
    const routeState = {};

    Object.values(pathConfig).forEach((paramConfig) => {
        objectSet(routeState, paramConfig.stateKey, paramConfig.initialState);
    });

    return objectMerge(acc, routeState);
}, {});

export const RosterPresets = {
    ALL: 'ALL',
    PLAYERS: 'PLAYERS',
    ALTERNATES: 'ALTERNATES',
} as const;

export type RosterPreset = typeof RosterPresets[keyof typeof RosterPresets];

export const MeasureTypeDisplayMap = {
    [MeasureTypes.LENGTH]: 'Length',
    [MeasureTypes.WEIGHT]: 'Weight',
    [MeasureTypes.TIME]: 'Time',
    [MeasureTypes.SPEED]: 'Speed',
} as const;

export const UnitDisplayMap = {
    [LengthUnits.FEET_INCHES]: 'ft/in',
    [LengthUnits.METERS]: 'm',
    [WeightUnits.LBS]: 'lbs',
    [WeightUnits.KG]: 'kg',
    [TimeUnits.HH_MM_SS]: 'HH:MM:SS',
    [TimeUnits.MM_SS_SSS]: 'MM:SS.sss',
    [TimeUnits.SS_SSS]: 'SS.sss',
    [SpeedUnits.MPH]: 'mph',
    [SpeedUnits.KPH]: 'kph',
} as const;
